import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { icon, notifyIcon, number, placeholder, quote } from "../../config/constants";
import { getWorkflowUrl } from "../../helper/common";
import { editWorkflowForm, generateFormDescription } from "../../shared/services/workflow.services";
import { getNotification } from "../../utils/common";
import { useWorkflowContext } from "../Workflow/WorkflowContextProvider";
import { useVariablesContext } from "../Workflow/WorkflowVariables/context/variableContext";
import FormSettings from "./FormSettings";
import FormSharePopup from "./FormSharePopup";
import FormPreview from "./Preview/FormPreview";
import UnsavedChangesModal from "./UnsavedChangesModal";
import { useFormChange } from "./contexts/FormChangeContext";
import { useFormMetadata } from "./contexts/FormMetadataContext";
import "./styles/FormBuilderHeader.scss";
import { createVariableDataObject, validateVariableConfiguration } from "./utils/variableValidation";

/**
 * Form Builder Header Component
 * Contains form title, save actions, and form-level controls
 */
const FormBuilderHeader = ({
  handleCloseModal,
  formId,
  isNewForm,
  formData,
  currentFormData,
  isEditable,
  setIsEditable,
  projectId,
  onFormBgColorChange,
  onSave,
  onSaveVariables,
  formTabs,
  setFormTabs,
  workflowId: workflowIdProp,
  onNavigateToWorkflowEditor,
  openFormSettingsOnRestore,
  onConsumeOpenFormSettingsRestore,
}) => {
  const { formTitle, setFormTitle, formDescription, setFormDescription, formPrivacy, setFormPrivacy } = useFormMetadata();
  const { hasChanges } = useFormChange();
  const variablesContext = useVariablesContext();
  const workflowContext = useWorkflowContext();
  const { hasVarChanges, setHasVarChanges } = variablesContext;
  const dispatch = useDispatch();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isFormSettingsOpen, setIsFormSettingsOpen] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const pendingAfterUnsavedModalRef = useRef(null);
  const projectSettings = useSelector((state) => state.projectSettings);
  const { user } = useSelector((state) => state.auth);
  const { allProjectsList } = useSelector((state) => state.tasks.defaultDetails);
  const history = useHistory();
  const location = useLocation();
  const [isFormHidden, setIsFormHidden] = useState(formData?.isHidden || false);
  const [isFormEnabled, setIsFormEnabled] = useState(isNewForm ? true : currentFormData?.isActive);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);

  // Form share popup state
  const [showFormSharePopup, setShowFormSharePopup] = useState(false);
  const shareButtonRef = useRef();

  const defaultProjectSettings = useMemo(
    () => [
      {
        value: 1,
        label: "Organisation",
        Icon: "OPEN_LOCK",
      },
      {
        value: 2,
        label: "Workspace",
        Icon: "TASK_LOCKED_OUTLINED",
      },
      {
        value: 3,
        label: "Team",
        Icon: "TASK_LOCK",
      },
    ],
    []
  );

  const privacyOptions = useMemo(() => {
    return projectSettings?.privacyList || defaultProjectSettings;
  }, [projectSettings?.privacyList]);
  const [privacyLevel, setPrivacyLevel] = useState(privacyOptions[0]);

  useEffect(() => {
    if (privacyOptions && privacyOptions.length > 0) {
      if (isNewForm) {
        const isMyBoard = projectId != null && user?.myProjectId != null && parseInt(projectId, 10) === parseInt(user.myProjectId, 10);
        const defaultPrivacy = isMyBoard
          ? privacyOptions.find((p) => p.value === number.THREE) || privacyOptions[2] || privacyOptions[0]
          : privacyOptions[1];
        setPrivacyLevel(defaultPrivacy);
        setFormPrivacy(defaultPrivacy);
      } else {
        const foundPrivacy = privacyOptions?.find((p) => p.value == currentFormData?.privacy);
        const level = foundPrivacy || privacyOptions[1];
        setPrivacyLevel(level);
        setFormPrivacy(level);
      }
    }
  }, [currentFormData, privacyOptions, isNewForm, projectId, user?.myProjectId]);

  // Sync form enabled state with current form data
  useEffect(() => {
    if (currentFormData && !isNewForm) {
      setIsFormEnabled(currentFormData.isActive);
      setIsFormHidden(currentFormData.isHiddenSection || false);
      setIsDataLoaded(true);
    } else if (isNewForm) {
      setIsDataLoaded(true);
    }
  }, [currentFormData, isNewForm]);

  useEffect(() => {
    if (openFormSettingsOnRestore) {
      setIsFormSettingsOpen(true);
      onConsumeOpenFormSettingsRestore?.();
    }
  }, [openFormSettingsOnRestore]);

  const executeNavigateToWorkflowEditor = () => {
    const wfId = currentFormData?.workflowId ?? workflowIdProp;
    if (!wfId) {
      return;
    }
    if (onNavigateToWorkflowEditor) {
      onNavigateToWorkflowEditor({ reopenFormSettings: isFormSettingsOpen });
      return;
    }
    const currentProject = allProjectsList?.find((p) => p.ProjectId == parseInt(projectId, 10));
    const projectName = currentProject?.ProjectName ?? currentProject?.Name ?? "";
    history.push(getWorkflowUrl(projectId, projectName, wfId), {
      returnPath: `${location.pathname}${location.search || ""}`,
    });
  };

  const handleTitleClick = () => {
    setIsEditingTitle(true);
  };

  const handleTitleChange = (e) => {
    setFormTitle(e.target.value);
  };

  const handleTitleBlur = () => {
    if (formTitle) {
      setIsEditingTitle(false);
    }
  };

  const handleTitleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (formTitle) {
        setIsEditingTitle(false);
      }
    }
  };

  const handleDescriptionClick = () => {
    setIsEditingDescription(true);
  };

  const handleDescriptionChange = (e) => {
    setFormDescription(e.target.value);
  };

  const handleDescriptionBlur = () => {
    setIsEditingDescription(false);
  };

  const handleDescriptionKeyPress = (e) => {
    if (e.key === "Enter") {
      setIsEditingDescription(false);
    }
  };

  const handleGenerateDescription = async () => {
    setIsGeneratingDescription(true);
    try {
      const response = await generateFormDescription({ id: formId });
      if (response?.description) {
        setFormDescription(response.description);
        getNotification(quote.ON_UPDATE, notifyIcon.SUCCESS_ICON);
      }
    } catch (error) {
      console.error("Error generating form description:", error);
      getNotification(quote.ON_ERROR, notifyIcon.ERROR_ICON);
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handlePreview = () => {
    setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
  };

  const handleFormSettings = () => {
    setIsFormSettingsOpen(true);
  };

  const handleCloseFormSettings = () => {
    setIsFormSettingsOpen(false);
  };

  const handleShare = () => {
    // Only allow sharing if form is saved (not new)
    if (!isNewForm && formId) {
      setShowFormSharePopup(true);
    }
  };

  const handleFormDeleted = () => {
    // Form was deleted, close the modal
    setIsFormSettingsOpen(false); // Close settings sidebar too
    handleCloseModal();
  };

  const handleIsEditable = async () => {
    if (isNewForm || !formId) {
      setIsEditable(!isEditable);
      return;
    }
    try {
      const updatePayload = {
        id: formId,
        workflowId: currentFormData?.workflowId,
        isEditable: !isEditable,
      };
      const response = await dispatch(editWorkflowForm(updatePayload));
      if (response) {
        setIsEditable(!isEditable);
      }
    } catch (error) {
      console.error("Error updating form status:", error);
    }
  };

  const handlePrivacyChange = async (privacyValue) => {
    if (isNewForm || !formId) {
      setPrivacyLevel(privacyOptions?.find((p) => p.value == privacyValue));
      setFormPrivacy(privacyOptions?.find((p) => p.value == privacyValue));
      return;
    }

    try {
      const updatePayload = {
        id: formId,
        workflowId: currentFormData?.workflowId,
        privacy: privacyValue,
      };
      const response = await dispatch(editWorkflowForm(updatePayload));
      if (response) {
        setPrivacyLevel(privacyOptions?.find((p) => p.value == privacyValue));
        setFormPrivacy(privacyOptions?.find((p) => p.value == privacyValue));
      }
    } catch (error) {
      console.error("Error updating form status:", error);
    }
  };

  /**
   * Check if there are any unsaved changes
   */
  const hasUnsavedChanges = () => {
    return (hasVarChanges && formTitle) || (hasChanges && formTitle);
  };

  const handleRequestEditWorkflow = () => {
    const wfId = currentFormData?.workflowId ?? workflowIdProp;
    if (!wfId) {
      return;
    }
    if (hasUnsavedChanges()) {
      pendingAfterUnsavedModalRef.current = "workflow";
      setShowUnsavedChangesModal(true);
    } else {
      executeNavigateToWorkflowEditor();
    }
  };

  /**
   * Handle close button click - check for unsaved changes first
   */
  const handleClose = () => {
    if (hasUnsavedChanges()) {
      pendingAfterUnsavedModalRef.current = "close";
      setShowUnsavedChangesModal(true);
    } else {
      handleCloseModal();
    }
  };

  /**
   * Handle saving changes from unsaved changes modal
   * Saves changes and closes the form builder for both new and existing forms
   * @author Bhavana
   */
  const handleSaveFromModal = async () => {
    setIsSavingChanges(true);

    try {
      if (hasVarChanges) {
        const variableData = createVariableDataObject({
          ...variablesContext,
          ...workflowContext,
        });

        const isValidVariable = validateVariableConfiguration(variableData, setHasVarChanges);
        if (!isValidVariable) {
          setIsSavingChanges(false);
          return;
        }
        await onSaveVariables();
        setHasVarChanges(false);
      }

      if (hasChanges) {
        await onSave();
      }

      setShowUnsavedChangesModal(false);
      const pending = pendingAfterUnsavedModalRef.current;
      pendingAfterUnsavedModalRef.current = null;
      if (pending === "workflow") {
        executeNavigateToWorkflowEditor();
        return;
      }
      handleCloseModal();
    } catch (error) {
      console.error("Error saving changes:", error);
    } finally {
      setIsSavingChanges(false);
    }
  };

  /**
   * Handle exit without saving from unsaved changes modal
   * @author Bhavana
   */
  const handleExitWithoutSaving = () => {
    setShowUnsavedChangesModal(false);
    const pending = pendingAfterUnsavedModalRef.current;
    pendingAfterUnsavedModalRef.current = null;
    if (pending === "workflow") {
      executeNavigateToWorkflowEditor();
      return;
    }
    handleCloseModal();
  };

  /**
   * Handle closing the unsaved changes modal (clicking outside)
   * @author Bhavana
   */
  const handleCloseUnsavedModal = () => {
    setShowUnsavedChangesModal(false);
    pendingAfterUnsavedModalRef.current = null;
  };

  return (
    <>
      <header className='form-builder-header'>
        <Tooltip anchorElement='target' position='bottom' parentTitle='true'>
          <div className='header-content d-flex justify-content-between align-items-center'>
            <div className='header-left'>
              {isEditingTitle ? (
                <input
                  type='text'
                  value={formTitle}
                  maxLength={30}
                  onChange={handleTitleChange}
                  onBlur={handleTitleBlur}
                  onKeyPress={handleTitleKeyPress}
                  className='form-title-input'
                  autoFocus
                />
              ) : (
                <h1 className='form-title' onClick={handleTitleClick}>
                  {formTitle}
                </h1>
              )}
              <div className='form-description-container d-flex align-items-center'>
                {isEditingDescription ? (
                  <input
                    type='text'
                    value={formDescription}
                    maxLength={90}
                    onChange={handleDescriptionChange}
                    onBlur={handleDescriptionBlur}
                    onKeyPress={handleDescriptionKeyPress}
                    className='form-description-input'
                    placeholder={placeholder?.WRITE_A_BRIEF_DESCRIPTION}
                    autoFocus
                  />
                ) : (
                  <span className='form-description' onClick={handleDescriptionClick}>
                    {formDescription || placeholder?.WRITE_A_BRIEF_DESCRIPTION}
                  </span>
                )}
                {!isNewForm && formId && (
                  <button
                    className='generate-description-btn'
                    onClick={handleGenerateDescription}
                    title={isGeneratingDescription ? "Generating..." : "Generate with AI"}
                    disabled={isGeneratingDescription}>
                    {isGeneratingDescription ? icon.REFRESH : icon.CIRCLE_STAR}
                  </button>
                )}
              </div>
            </div>
            <div className='header-center'>
              {isDataLoaded && !isFormEnabled && !isNewForm && <span className='form-status-text red'>Form disabled - not accepting inputs</span>}
            </div>
            <div className='header-actions justify-content-end'>
              <div className=''>
                <div className='d-flex justify-content-between align-items-center'>
                  <span className='status-bar'>{`This form ${isEditable ? "can" : "cannot"} be edited after submission.`}</span>
                </div>
              </div>
              <button
                className={`header-lock-icon-btn header-icon-btn d-flex align-items-center justify-content-center ${
                  isEditable ? "lock-open" : "lock-closed"
                }`}
                onClick={handleIsEditable}
                title='Lock Form'>
                {isEditable ? icon.LOCK_ICON_OPEN : icon.LOCK_ICON}
              </button>
              <span className='privacy-status-text'>Privacy: {privacyLevel?.label?.split(" (")[0]?.toLowerCase()}</span>
              <button className='preview-btn d-flex align-items-center justify-content-center' onClick={handlePreview} title='Preview'>
                {icon.VISIBILITY}
                <span className='preview-text'>Preview</span>
              </button>
              <button
                className={`header-icon-btn d-flex align-items-center justify-content-center ${isNewForm ? "disabled" : ""}`}
                ref={shareButtonRef}
                onClick={handleShare}
                disabled={isNewForm || !isFormEnabled}
                title={isNewForm ? "Save form first to share" : "Share"}>
                {icon.SHARE}
              </button>
              <button className='header-icon-btn d-flex align-items-center justify-content-center' onClick={handleFormSettings} title='Form Settings'>
                {icon.WORKFLOW_29}
              </button>
              <button className='header-icon-btn close-btn d-flex align-items-center justify-content-center mr-2' onClick={handleClose} title='Close'>
                {icon.CLOSE}
              </button>
            </div>
          </div>
        </Tooltip>
      </header>

      <FormPreview
        isOpen={isPreviewOpen}
        onClose={handleClosePreview}
        formData={formData}
        formTitle={formTitle}
        formId={formId}
        formDescription={formDescription}
        setFormTab={setFormTabs}
        formTab={formTabs}
      />

      <FormSettings
        isOpen={isFormSettingsOpen}
        onClose={handleCloseFormSettings}
        onEditWorkflow={handleRequestEditWorkflow}
        formId={formId}
        isNewForm={isNewForm}
        currentFormData={currentFormData}
        formData={formData}
        onFormBgColorChange={onFormBgColorChange}
        isFormEnabled={isFormEnabled}
        setIsFormEnabled={setIsFormEnabled}
        isFormHidden={isFormHidden}
        setIsFormHidden={setIsFormHidden}
        privacyLevel={privacyLevel}
        setPrivacyLevel={setPrivacyLevel}
        onFormDeleted={handleFormDeleted}
        onPrivacyChange={handlePrivacyChange}
        privacyOptions={privacyOptions}
        projectSettings={projectSettings}
        user={user}
        projectId={projectId}
        setFormTabs={setFormTabs}
      />

      {showFormSharePopup && !isNewForm && formId && (
        <FormSharePopup
          showPopup={showFormSharePopup}
          shareIconAnchor={shareButtonRef}
          formId={formId}
          formName={formTitle || currentFormData?.title || "Untitled Form"}
          closeCopyLinkPopup={() => setShowFormSharePopup(false)}
          setShowSharePopup={setShowFormSharePopup}
        />
      )}

      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        onSave={handleSaveFromModal}
        onExit={handleExitWithoutSaving}
        onClose={handleCloseUnsavedModal}
        isSaving={isSavingChanges}
      />
    </>
  );
};

export default FormBuilderHeader;
